/**
 * mappingModel.ts
 * Admin → Test Mapping (master-detail redesign, v1.0.34) — PURE view-model
 * logic. No React, no API calls — unit-tested by
 * src/__tests__/testMappingStudio.test.ts.
 *
 * Status semantics (one per lab test):
 *   unmapped    — no map row, no exact-name match → NOT schedulable (amber, bold)
 *   unconfirmed — active map row, IsConfirmed=0 (amber outline, D4 estimate)
 *   confirmed   — active map row, IsConfirmed=1 (sage)
 *   nameMatch   — no row; resolves by exact TestName match (grey, still schedulable)
 */

import type {
  MappingStudioTest,
  MappingSuggestion,
} from '../../../services/testMappingService';

// ── Status derivation ─────────────────────────────────────────────────────────

export type TestMappingStatus = 'unmapped' | 'unconfirmed' | 'confirmed' | 'nameMatch';

export function deriveTestStatus(t: MappingStudioTest): TestMappingStatus {
  if (t.mapping) return t.mapping.IsConfirmed ? 'confirmed' : 'unconfirmed';
  return t.exactNameMatchKey !== null ? 'nameMatch' : 'unmapped';
}

/** A test is resolved (schedulable) when it has a map row OR an exact-name match. */
export const isTestResolved = (t: MappingStudioTest): boolean =>
  t.mapping !== null || t.exactNameMatchKey !== null;

// ── Left-column list: filter + sort ──────────────────────────────────────────

export type TestListFilter = 'all' | 'unmapped' | 'unconfirmed' | 'confirmed';

/**
 * Which statuses each filter chip shows. "Confirmed" includes nameMatch —
 * both are resolved with no pending action (nameMatch keeps its own grey
 * chip in the row so the difference stays visible).
 */
const FILTER_STATUSES: Record<TestListFilter, readonly TestMappingStatus[]> = {
  all: ['unmapped', 'unconfirmed', 'confirmed', 'nameMatch'],
  unmapped: ['unmapped'],
  unconfirmed: ['unconfirmed'],
  confirmed: ['confirmed', 'nameMatch'],
};

/** Attention-first sort rank: unmapped, then unconfirmed, then the rest. */
const STATUS_RANK: Record<TestMappingStatus, number> = {
  unmapped: 0,
  unconfirmed: 1,
  confirmed: 2,
  nameMatch: 2,
};

/**
 * Filter the lab-test list by search text (case-insensitive substring of the
 * test name) + status filter, sorted unmapped-first, then unconfirmed, then
 * alphabetical within each rank.
 */
export function filterAndSortTests(
  tests: MappingStudioTest[],
  search: string,
  filter: TestListFilter,
): MappingStudioTest[] {
  const needle = search.trim().toLowerCase();
  const allowed = FILTER_STATUSES[filter];
  return tests
    .filter((t) =>
      (needle === '' || t.TestName.toLowerCase().includes(needle))
      && allowed.includes(deriveTestStatus(t)))
    .sort((a, b) =>
      STATUS_RANK[deriveTestStatus(a)] - STATUS_RANK[deriveTestStatus(b)]
      || a.TestName.localeCompare(b.TestName));
}

/** Count per filter chip (search-independent — chips show dataset totals). */
export function countByFilter(tests: MappingStudioTest[]): Record<TestListFilter, number> {
  const counts: Record<TestListFilter, number> = {
    all: tests.length, unmapped: 0, unconfirmed: 0, confirmed: 0,
  };
  for (const t of tests) {
    const status = deriveTestStatus(t);
    if (status === 'unmapped') counts.unmapped++;
    else if (status === 'unconfirmed') counts.unconfirmed++;
    else counts.confirmed++; // confirmed + nameMatch, matching FILTER_STATUSES
  }
  return counts;
}

// ── Stats (header chips) ──────────────────────────────────────────────────────

export interface MappingStats {
  totalTests: number;
  /** Resolved tests (map row or exact-name match). */
  mappedCount: number;
  /** Mapped via an UNCONFIRMED estimate (D4 amber). */
  unconfirmedCount: number;
  unmappedCount: number;
  /** Active work-order lines sitting on UNMAPPED tests — the blast radius. */
  unmappedWoLines: number;
}

export function computeMappingStats(tests: MappingStudioTest[]): MappingStats {
  let mappedCount = 0;
  let unconfirmedCount = 0;
  let unmappedWoLines = 0;
  for (const t of tests) {
    if (isTestResolved(t)) {
      mappedCount++;
      if (t.mapping && !t.mapping.IsConfirmed) unconfirmedCount++;
    } else {
      unmappedWoLines += t.woLineCount;
    }
  }
  return {
    totalTests: tests.length,
    mappedCount,
    unconfirmedCount,
    unmappedCount: tests.length - mappedCount,
    unmappedWoLines,
  };
}

// ── Suggestions reducer ───────────────────────────────────────────────────────

export type SuggestionsAction =
  | { type: 'loaded'; suggestions: MappingSuggestion[] }
  /** A candidate was accepted (POSTed) — the test's suggestion row is spent. */
  | { type: 'accepted'; testKey: string }
  | { type: 'clear' };

/**
 * Pure reducer for the per-test suggestions cache. The /suggestions endpoint
 * returns candidates for every UNRESOLVED test in one call; the detail pane
 * looks its test up by key. Rows with zero candidates are the server saying
 * "no catalog item is close enough" (the PSD class) — kept so the pane can
 * explain instead of showing nothing.
 */
export function suggestionsReducer(
  state: MappingSuggestion[],
  action: SuggestionsAction,
): MappingSuggestion[] {
  switch (action.type) {
    case 'loaded':
      return action.suggestions;
    case 'accepted':
      return state.filter((s) => s.testKey !== action.testKey);
    case 'clear':
      return [];
    default:
      return state;
  }
}
